<template>
  <!-- Live states (seeding / running): animated green dot -->
  <span
    v-if="isLive"
    class="debate-state-badge debate-state-badge--live"
    :aria-label="t('debates.state.live')"
  >
    <span class="debate-state-badge__dot debate-state-badge__dot--animated" aria-hidden="true" />
    <span class="debate-state-badge__label">{{ t('debates.state.live') }}</span>
  </span>

  <!-- Paused state: stone/muted chip with inanimated dot (D1 — PO decision) -->
  <span
    v-else-if="isPaused"
    class="debate-state-badge debate-state-badge--paused"
    :aria-label="t('debates.state.paused')"
  >
    <span class="debate-state-badge__dot" aria-hidden="true" />
    <span class="debate-state-badge__label">{{ t('debates.state.paused') }}</span>
  </span>

  <!-- Other non-live states: KdsBadge -->
  <KdsBadge
    v-else
    :tone="tone"
    class="debate-state-badge"
  >
    {{ label }}
  </KdsBadge>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { KdsBadge } from '@/kds';
import type { KdsBadgeTone } from '@/kds/types';
import type { DebateState } from '@/types/debate';

const props = defineProps<{
  state: DebateState;
}>();

const { t } = useI18n();

const isLive = computed(() => props.state === 'seeding' || props.state === 'running');
const isPaused = computed(() => props.state === 'paused');

const tone = computed<KdsBadgeTone>(() => {
  switch (props.state) {
    case 'complete': return 'ok';
    case 'failed': return 'danger';
    case 'pending': return 'muted';
    default: return 'muted';
  }
});

const label = computed(() => {
  switch (props.state) {
    case 'complete': return t('debates.state.complete');
    case 'failed': return t('debates.state.failed');
    case 'pending': return t('debates.state.draft');
    default: return props.state;
  }
});
</script>

<style scoped lang="scss">
.debate-state-badge {
  // Live chip: Signal green background, animated dot
  &--live {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    background: var(--signal-tint);
    border: 1px solid var(--signal-press);
    border-radius: var(--r-pill);
    padding: 2px var(--space-3);
    font-family: var(--font-mono);
    font-size: var(--fs-mono);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ink);
  }

  // Paused chip: stone/neutral, no animation (D1 — aligns with spec)
  &--paused {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    background: var(--paper-3);
    border: 1px solid var(--mist-2);
    border-radius: var(--r-pill);
    padding: 2px var(--space-3);
    font-family: var(--font-mono);
    font-size: var(--fs-mono);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--stone);
  }

  // Static dot (paused — stone color, no animation)
  &__dot {
    width: 7px;
    height: 7px;
    border-radius: var(--r-pill);
    background: var(--stone-2);
    flex-shrink: 0;
  }

  // Animated dot (live states only)
  &__dot--animated {
    background: var(--signal);
    animation: debate-badge-pulse 1.4s var(--ease-in-out) infinite;
  }

  &__label {
    line-height: 1;
  }
}

@keyframes debate-badge-pulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.45; }
}

@media (prefers-reduced-motion: reduce) {
  .debate-state-badge__dot--animated {
    animation: none;
  }
}
</style>
